import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Autocomplete,
  Stack,
  Chip,
  Collapse,
  IconButton,
  CircularProgress,
  Link,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';

type AutoRedeemStatus = 'disabled' | 'pending' | 'completed' | 'failed';

interface AutoRedeemBlock {
  status: AutoRedeemStatus;
  requested_amount: number | null;
  balance_before: number | null;
  balance_after: number | null;
  debited: number | null;
  platform: string | null;
  platform_request_id: number | null;
  message: string | null;
  enqueued_at: string | null;
  completed_at: string | null;
}

interface AutoRedeem {
  transaction_id: number;
  user_id: number;
  user_email: string | null;
  amount: number | null;
  tx_status: string;
  initiated_at: string | null;
  platform: string | null;
  payout_method: string | null;
  payout_address: string | null;
  auto_redeem: AutoRedeemBlock;
}

// Shape returned by legacy backends (v3/main) that don't have the auto-redeem feature.
interface LegacyRedeem {
  id: number;
  user_email: string;
  amount: number;
  status: string;
  platform: string;
  payout_method: string;
  payout_address: string;
  initiated_at: string;
  completed_at?: string;
  description?: string;
}

interface LegacyPagination {
  total_pages: number;
  has_next: boolean;
  has_previous: boolean;
}

interface TenantOption {
  id: number;
  name: string;
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  credit_balance: number;
  tenant_id: number;
}

interface ManualRedeemForm {
  user_email: string;
  amount: string;
  platform: string;
  payout_method: string;
  payout_address: string;
  description: string;
}

interface RedeemsPageProps {
  tenantId?: number;
}

const PAGE_SIZE = 100;
const POLL_INTERVAL_MS = 5000;

const STATUS_FILTERS: { label: string; value: AutoRedeemStatus | '' }[] = [
  { label: 'All', value: '' },
  { label: 'Disabled', value: 'disabled' },
  { label: 'Pending', value: 'pending' },
  { label: 'Completed', value: 'completed' },
  { label: 'Failed', value: 'failed' },
];

// MUI Chip colors per the auto-redeem status table.
const autoRedeemChipColor = (
  status: AutoRedeemStatus
): 'default' | 'warning' | 'success' | 'error' => {
  switch (status) {
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    case 'pending':
      return 'warning';
    default:
      return 'default';
  }
};

const payoutColor = (status: string): string => {
  const s = (status || '').toLowerCase();
  if (s === 'success') return 'green';
  if (s === 'pending' || s === 'processing') return 'orange';
  if (s === 'failed' || s === 'cancelled') return 'red';
  return 'inherit';
};

const fmtMoney = (v: number | null | undefined): string =>
  v === null || v === undefined ? '-' : `$${v.toFixed(2)}`;

const fmtDate = (v: string | null | undefined): string =>
  v ? new Date(v).toLocaleString() : '-';

const RedeemsPage: React.FC<RedeemsPageProps> = ({ tenantId }) => {
  const isSuperAdmin = localStorage.getItem('userRole') === 'super_admin';
  const userTenantIdStr = localStorage.getItem('userTenantId');

  // Resolve the active tenant: prop (tenant-admin panel) > super-admin selection > own tenant id.
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<number | ''>(() => {
    if (tenantId) return tenantId;
    if (!isSuperAdmin) {
      const tid = parseInt(userTenantIdStr || '0', 10);
      return tid > 0 ? tid : '';
    }
    return '';
  });

  const navigate = useNavigate();

  const [redeems, setRedeems] = useState<AutoRedeem[]>([]);
  // Backward compat: detect whether the backend supports the auto-redeem feature.
  const [mode, setMode] = useState<'auto' | 'legacy' | null>(null);
  const [legacyRedeems, setLegacyRedeems] = useState<LegacyRedeem[]>([]);
  const [legacyPagination, setLegacyPagination] = useState<LegacyPagination>({
    total_pages: 1,
    has_next: false,
    has_previous: false,
  });
  const [statusFilter, setStatusFilter] = useState<AutoRedeemStatus | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' }>(
    { open: false, message: '', severity: 'success' }
  );

  const [manualRedeemDialog, setManualRedeemDialog] = useState(false);
  const [userSearchResults, setUserSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [availablePlatforms, setAvailablePlatforms] = useState<string[]>([]);
  const [manualRedeemForm, setManualRedeemForm] = useState<ManualRedeemForm>({
    user_email: '',
    amount: '',
    platform: '',
    payout_method: '',
    payout_address: '',
    description: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Latest fetch params, used by the polling interval without re-creating it each render.
  const fetchRef = useRef<() => void>(() => {});
  // Date filters are applied via the Search button, so the fetch reads them from refs
  // (kept in sync with the inputs) rather than re-fetching on every keystroke.
  const startDateRef = useRef('');
  const endDateRef = useRef('');

  const showTenantSelector = isSuperAdmin && !tenantId;

  const fetchTenants = useCallback(async () => {
    try {
      const res = await api.get<TenantOption[]>('/api/v1/admin/tenants/');
      setTenants(res.data || []);
    } catch {
      setSnackbar({ open: true, message: 'Failed to load tenants', severity: 'error' });
    }
  }, []);

  const fetchRedeems = useCallback(async () => {
    if (selectedTenant === '') return;
    setLoading(true);
    try {
      // Superset of params so both the new (limit/offset/status) and legacy (page/page_size)
      // backends each pick what they understand.
      const params: Record<string, any> = {
        tenant_id: selectedTenant,
        limit: PAGE_SIZE,
        offset,
        page: Math.floor(offset / PAGE_SIZE) + 1,
        page_size: PAGE_SIZE,
      };
      if (statusFilter) params.status = statusFilter;
      if (startDateRef.current) params.start_date = startDateRef.current;
      if (endDateRef.current) params.end_date = endDateRef.current;
      const res = await api.get('/api/v1/admin/redeems', { params });
      const data = res.data as any;
      if (Array.isArray(data)) {
        // The v2_apis legacy backend also returns a bare array, but its items are
        // legacy-shaped (no `auto_redeem` block). Use that to tell it apart from the
        // new auto-redeem backend. Empty arrays stay 'auto' to preserve the new
        // backend's zero-results behavior.
        const looksLegacy =
          data.length > 0 &&
          data[0] != null &&
          typeof data[0] === 'object' &&
          !('auto_redeem' in data[0]);
        if (looksLegacy) {
          // v2_apis legacy backend: bare array of legacy redeems, no pagination envelope.
          setMode('legacy');
          setLegacyRedeems(data as LegacyRedeem[]);
          setLegacyPagination({ total_pages: 1, has_next: false, has_previous: false });
        } else {
          // New backend: array of auto-redeems.
          setMode('auto');
          setRedeems(data as AutoRedeem[]);
        }
      } else if (data && Array.isArray(data.items)) {
        // Paginated { items, pagination }. The unified v3 backend now serves the
        // auto-redeem shape inside this envelope, so route on the item shape rather
        // than assuming legacy — otherwise the auto-redeem UI silently disappears.
        // Same convention as the bare-array branch: empty stays 'auto'.
        const items = data.items as any[];
        const looksLegacy =
          items.length > 0 &&
          items[0] != null &&
          typeof items[0] === 'object' &&
          !('auto_redeem' in items[0]);
        if (looksLegacy) {
          setMode('legacy');
          setLegacyRedeems(items as LegacyRedeem[]);
          const p = data.pagination || {};
          setLegacyPagination({
            total_pages: p.total_pages ?? 1,
            has_next: p.has_next ?? false,
            has_previous: p.has_previous ?? false,
          });
        } else {
          setMode('auto');
          setRedeems(items as AutoRedeem[]);
        }
      } else {
        setRedeems([]);
        setLegacyRedeems([]);
      }
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to fetch redeems';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, statusFilter, offset]);

  const fetchAvailablePlatforms = useCallback(async () => {
    try {
      const res = await api.get('/api/v1/admin/platforms/all');
      setAvailablePlatforms((res.data as any[]).map((p: any) => p.name));
    } catch {
      setAvailablePlatforms([]);
    }
  }, []);

  useEffect(() => {
    if (showTenantSelector) fetchTenants();
  }, [showTenantSelector, fetchTenants]);

  useEffect(() => {
    fetchAvailablePlatforms();
  }, [fetchAvailablePlatforms]);

  useEffect(() => {
    fetchRedeems();
  }, [fetchRedeems]);

  // Keep a ref to the freshest fetch so the polling interval stays stable.
  useEffect(() => {
    fetchRef.current = fetchRedeems;
  }, [fetchRedeems]);

  // Auto-refresh while any visible redeem is still pending.
  const hasPending = redeems.some(r => r.auto_redeem?.status === 'pending');
  useEffect(() => {
    if (!hasPending) return;
    const interval = setInterval(() => fetchRef.current(), POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [hasPending]);

  const handleStatusFilter = (value: AutoRedeemStatus | '') => {
    setStatusFilter(value);
    setOffset(0);
  };

  // Apply the date range. Resetting offset triggers a re-fetch; if already at 0, fetch directly.
  const handleSearch = () => {
    if (offset !== 0) setOffset(0);
    else fetchRedeems();
  };

  const handleEmailClick = (email: string | null) => {
    if (email) navigate(`/users?search=${encodeURIComponent(email)}`);
  };

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePayoutStatusChange = async (id: number, status: string) => {
    try {
      const res = await api.post<{ message?: string; credits_returned?: boolean }>(
        `/api/v1/admin/redeems/${id}/status`,
        { status },
      );
      // The API's own message is the only place the outcome is explained.
      // Approving a DollarPay payout leaves the row PENDING on purpose -
      // DollarPay has accepted the order, not confirmed payment - and rejecting
      // one that was already sent returns credits_returned: false because the
      // money may already be gone. A fixed "updated" toast hides both.
      setSnackbar({
        open: true,
        message: res.data?.message || 'Payout status updated',
        severity: res.data?.credits_returned === false ? 'warning' : 'success',
      });
      fetchRedeems();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const searchUsers = async (email: string) => {
    if (email.length < 2) {
      setUserSearchResults([]);
      return;
    }
    try {
      const res = await api.get(`/api/v1/admin/users/search?email=${encodeURIComponent(email)}`);
      setUserSearchResults(res.data as User[]);
    } catch {
      setSnackbar({ open: true, message: 'Failed to search users', severity: 'error' });
    }
  };

  const resetManualRedeemForm = () => {
    setManualRedeemForm({
      user_email: '',
      amount: '',
      platform: '',
      payout_method: '',
      payout_address: '',
      description: '',
    });
    setSelectedUser(null);
    setUserSearchResults([]);
  };

  const handleCreateManualRedeem = async () => {
    if (!selectedUser) {
      setSnackbar({ open: true, message: 'Please select a user', severity: 'error' });
      return;
    }
    if (!manualRedeemForm.amount || !manualRedeemForm.platform || !manualRedeemForm.payout_method || !manualRedeemForm.payout_address) {
      setSnackbar({ open: true, message: 'Please fill in all required fields', severity: 'error' });
      return;
    }
    const amount = parseFloat(manualRedeemForm.amount);
    if (isNaN(amount) || amount <= 0) {
      setSnackbar({ open: true, message: 'Please enter a valid amount', severity: 'error' });
      return;
    }

    setIsSubmitting(true);
    try {
      await api.post('/api/v1/admin/redeems/manual', {
        user_email: selectedUser.email,
        amount,
        platform: manualRedeemForm.platform,
        payout_method: manualRedeemForm.payout_method,
        payout_address: manualRedeemForm.payout_address,
        description: manualRedeemForm.description,
      });
      setSnackbar({ open: true, message: 'Manual redeem created successfully', severity: 'success' });
      setManualRedeemDialog(false);
      resetManualRedeemForm();
      fetchRedeems();
    } catch (error: any) {
      const message = error.response?.data?.detail || 'Failed to create manual redeem';
      setSnackbar({ open: true, message, severity: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const COL_COUNT = 11;

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Auto-Redeems
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchRedeems()}>
            Refresh
          </Button>
          <Button variant="contained" color="primary" onClick={() => { setManualRedeemDialog(true); resetManualRedeemForm(); }}>
            Create Manual Redeem
          </Button>
        </Stack>
      </Box>

      {showTenantSelector && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 300 }} size="small">
            <InputLabel>Select Tenant</InputLabel>
            <Select
              value={selectedTenant}
              label="Select Tenant"
              onChange={e => { setSelectedTenant(Number(e.target.value)); setOffset(0); }}
            >
              {tenants.map(t => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {showTenantSelector && selectedTenant === '' ? (
        <Typography color="text.secondary">Select a tenant to view auto-redeems.</Typography>
      ) : (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Status chips only apply to the auto-redeem (new) backend. */}
            {mode !== 'legacy' && STATUS_FILTERS.map(f => (
              <Chip
                key={f.label}
                label={f.label}
                onClick={() => handleStatusFilter(f.value)}
                color={statusFilter === f.value ? 'primary' : 'default'}
                variant={statusFilter === f.value ? 'filled' : 'outlined'}
              />
            ))}
            {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Stack>

          <Stack direction="row" spacing={2} sx={{ mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); startDateRef.current = e.target.value; }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); endDateRef.current = e.target.value; }}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button variant="contained" onClick={handleSearch}>Search</Button>
          </Stack>

          {mode !== 'legacy' && (
          <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Txn ID</TableCell>
                  <TableCell>User ID</TableCell>
                  <TableCell>User Email</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payout</TableCell>
                  <TableCell>Payout Method</TableCell>
                  <TableCell>Payout Address</TableCell>
                  <TableCell>Auto-Redeem</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Initiated At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {redeems.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={COL_COUNT} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No redeems found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {redeems.map(r => {
                  const ar = r.auto_redeem || ({ status: 'disabled' } as AutoRedeemBlock);
                  const isOpen = expanded.has(r.transaction_id);
                  return (
                    <React.Fragment key={r.transaction_id}>
                      <TableRow hover>
                        <TableCell>
                          <IconButton size="small" onClick={() => toggleExpand(r.transaction_id)}>
                            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell>{r.transaction_id}</TableCell>
                        <TableCell>{r.user_id}</TableCell>
                        <TableCell>
                          {r.user_email ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => handleEmailClick(r.user_email)}
                              sx={{ color: 'primary.main', cursor: 'pointer' }}
                            >
                              {r.user_email}
                            </Link>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{fmtMoney(r.amount)}</TableCell>
                        <TableCell>
                          {(() => {
                            const v = (r.tx_status || '').toLowerCase();
                            const known = ['pending', 'success', 'failed'].includes(v);
                            return (
                              <Select
                                value={v}
                                onChange={e => handlePayoutStatusChange(r.transaction_id, e.target.value as string)}
                                size="small"
                                sx={{ color: payoutColor(r.tx_status), fontWeight: 600, minWidth: 120 }}
                              >
                                {!known && <MenuItem value={v} disabled>{v.toUpperCase()}</MenuItem>}
                                <MenuItem value="pending">PENDING</MenuItem>
                                <MenuItem value="success">SUCCESS</MenuItem>
                                <MenuItem value="failed">FAILED</MenuItem>
                              </Select>
                            );
                          })()}
                        </TableCell>
                        <TableCell>{r.payout_method || '-'}</TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}
                            title={r.payout_address || ''}
                          >
                            {r.payout_address || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={ar.status.toUpperCase()}
                            size="small"
                            color={autoRedeemChipColor(ar.status)}
                            icon={ar.status === 'pending' ? <CircularProgress size={14} color="inherit" /> : undefined}
                          />
                        </TableCell>
                        <TableCell>{r.platform || ar.platform || '-'}</TableCell>
                        <TableCell>{fmtDate(r.initiated_at)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ py: 0, borderBottom: isOpen ? undefined : 'none' }} colSpan={COL_COUNT}>
                          <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                              <Typography variant="subtitle2" gutterBottom>Redeem Log</Typography>

                              {ar.status === 'failed' && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                  Platform was NOT debited — handle this redeem manually.
                                  {ar.message ? ` (${ar.message})` : ''}
                                </Alert>
                              )}
                              {ar.status === 'disabled' && (
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  Manual platform — no auto action happened; process as before.
                                </Alert>
                              )}
                              {ar.status === 'completed' && (
                                <Alert severity="success" sx={{ mb: 2 }}>
                                  Platform already debited — only the payout needs to be fulfilled.
                                  {r.payout_method && r.payout_address
                                    ? ` Send via ${r.payout_method} to ${r.payout_address}.`
                                    : ''}
                                </Alert>
                              )}
                              {ar.status === 'pending' && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                  Auto-redeem queued — waiting for the worker to finish.
                                </Alert>
                              )}

                              <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', gap: 2, mb: 2 }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Requested</Typography>
                                  <Typography>{fmtMoney(ar.requested_amount)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Balance Before → After</Typography>
                                  <Typography>{fmtMoney(ar.balance_before)} → {fmtMoney(ar.balance_after)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Debited</Typography>
                                  <Typography>{fmtMoney(ar.debited)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Platform</Typography>
                                  <Typography>{r.platform || ar.platform || '-'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Payout Method</Typography>
                                  <Typography>{r.payout_method || '-'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Payout Address</Typography>
                                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{r.payout_address || '-'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Platform Request ID</Typography>
                                  <Typography>{ar.platform_request_id ?? '-'}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Enqueued At</Typography>
                                  <Typography>{fmtDate(ar.enqueued_at)}</Typography>
                                </Box>
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Completed At</Typography>
                                  <Typography>{fmtDate(ar.completed_at)}</Typography>
                                </Box>
                              </Stack>

                              {ar.message && ar.status !== 'failed' && (
                                <Typography variant="body2">
                                  <strong>Message:</strong> {ar.message}
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {/* Legacy backends (v3/main) without the auto-redeem feature. */}
          {mode === 'legacy' && (
          <TableContainer component={Paper} sx={{ maxWidth: '100%', overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  <TableCell>User Email</TableCell>
                  <TableCell>Platform</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Payout Method</TableCell>
                  <TableCell>Payout Address</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Initiated At</TableCell>
                  <TableCell>Completed At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {legacyRedeems.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No redeems found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {legacyRedeems.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => handleEmailClick(r.user_email)}
                        sx={{ color: 'primary.main', cursor: 'pointer' }}
                      >
                        {r.user_email}
                      </Link>
                    </TableCell>
                    <TableCell>{r.platform}</TableCell>
                    <TableCell>{fmtMoney(r.amount)}</TableCell>
                    <TableCell>{r.payout_method}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.75rem' }}
                        title={r.payout_address}
                      >
                        {r.payout_address}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography style={{ color: payoutColor(r.status), fontWeight: 600 }}>
                        {(r.status || '').toUpperCase()}
                      </Typography>
                    </TableCell>
                    <TableCell>{fmtDate(r.initiated_at)}</TableCell>
                    <TableCell>{r.completed_at ? fmtDate(r.completed_at) : '-'}</TableCell>
                    <TableCell>
                      <Select
                        value={(r.status || '').toLowerCase()}
                        onChange={e => handlePayoutStatusChange(r.id, e.target.value as string)}
                        size="small"
                        disabled={r.status === 'success' || r.status === 'failed'}
                      >
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="success">Success</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
            <Button
              variant="outlined"
              disabled={offset === 0 || loading}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Typography variant="body2">
              {mode === 'legacy'
                ? `Page ${Math.floor(offset / PAGE_SIZE) + 1} of ${legacyPagination.total_pages}`
                : `Showing ${redeems.length ? offset + 1 : 0}–${offset + redeems.length}`}
            </Typography>
            <Button
              variant="outlined"
              disabled={(mode === 'legacy' ? !legacyPagination.has_next : redeems.length < PAGE_SIZE) || loading}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </Box>
        </>
      )}

      {/* Manual Redeem Dialog */}
      <Dialog open={manualRedeemDialog} onClose={() => { setManualRedeemDialog(false); resetManualRedeemForm(); }} maxWidth="md" fullWidth>
        <DialogTitle>Create Manual Redeem</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              options={userSearchResults}
              getOptionLabel={(option) => `${option.email} (${option.first_name || 'N/A'} ${option.last_name || 'N/A'}) - Balance: $${option.credit_balance.toFixed(2)}`}
              value={selectedUser}
              onChange={(_, newValue) => {
                setSelectedUser(newValue);
                if (newValue) setManualRedeemForm(prev => ({ ...prev, user_email: newValue.email }));
              }}
              onInputChange={(_, newInputValue) => {
                setManualRedeemForm(prev => ({ ...prev, user_email: newInputValue }));
                searchUsers(newInputValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search User by Email" required helperText="Start typing to search for users" />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body1">{option.email}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option.first_name || 'N/A'} {option.last_name || 'N/A'} - Balance: ${option.credit_balance.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              )}
            />

            <TextField
              label="Amount ($)"
              type="number"
              value={manualRedeemForm.amount}
              onChange={(e) => setManualRedeemForm(prev => ({ ...prev, amount: e.target.value }))}
              required
              inputProps={{ min: 0.01, step: 0.01 }}
              helperText="Enter the redeem amount"
            />

            <FormControl required>
              <InputLabel>Platform</InputLabel>
              <Select
                value={manualRedeemForm.platform}
                onChange={(e) => setManualRedeemForm(prev => ({ ...prev, platform: e.target.value }))}
                label="Platform"
              >
                {availablePlatforms.map(p => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl required>
              <InputLabel>Payout Method</InputLabel>
              <Select
                value={manualRedeemForm.payout_method}
                onChange={(e) => setManualRedeemForm(prev => ({ ...prev, payout_method: e.target.value }))}
                label="Payout Method"
              >
                <MenuItem value="Crypto Wallet">Crypto Wallet</MenuItem>
                <MenuItem value="Cash Tag">Cash Tag</MenuItem>
                <MenuItem value="PayPal">PayPal</MenuItem>
                <MenuItem value="PsiFi">PsiFi</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Payout Address"
              value={manualRedeemForm.payout_address}
              onChange={(e) => setManualRedeemForm(prev => ({ ...prev, payout_address: e.target.value }))}
              required
              placeholder="Enter wallet address, cash tag, or PayPal email"
            />

            <TextField
              label="Description (Optional)"
              value={manualRedeemForm.description}
              onChange={(e) => setManualRedeemForm(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={3}
              placeholder="Additional notes about this redeem request"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setManualRedeemDialog(false); resetManualRedeemForm(); }} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleCreateManualRedeem} variant="contained" disabled={isSubmitting || !selectedUser}>
            {isSubmitting ? 'Creating...' : 'Create Redeem'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default RedeemsPage;
